/**
 * @module components/billing/PricingSection
 * @description
 *   Home page "Plans & credits" section — pay-per-use / subscribe toggle,
 *   credit quantity stepper, subscription tier cards, promo code, and the
 *   Stripe checkout CTA. Forwards its ref so the parent can scroll it into view.
 *
 * @extracted-from client/src/App.js (lines ~1822–2188, Phase 7)
 */

import React, { forwardRef } from 'react';
import { CheckCircle, Star, X, CreditCard, Lock, ChevronRight } from 'lucide-react';
import { PRICING_TIERS, SUBSCRIPTION_PLANS } from '../../constants/pricing';
import { SegmentedControl, Eyebrow, Alert } from '../ui';

/**
 * @param {object} props
 * @param {'one-time'|'subscription'} props.homePaymentType
 * @param {(type: 'one-time'|'subscription') => void} props.setHomePaymentType
 * @param {number} props.homePpuQuantity
 * @param {(updater: number | ((q: number) => number)) => void} props.setHomePpuQuantity
 * @param {string} props.homeSelectedSubTier — SUBSCRIPTION_PLANS id
 * @param {(id: string) => void} props.setHomeSelectedSubTier
 * @param {string} props.homePromoCode — coupon code input value
 * @param {(code: string) => void} props.setHomePromoCode
 * @param {object|null} props.homeAppliedCoupon — validated coupon from `/api/validate-coupon`
 * @param {string} [props.homeCouponError]
 * @param {boolean} [props.homeCouponLoading]
 * @param {boolean} props.homeCheckoutLoading
 * @param {() => void} props.onApplyCoupon
 * @param {() => void} [props.onRemoveCoupon]
 * @param {() => void} props.onCheckout
 * @param {() => void} [props.onManageBilling]
 * @param {{ email?: string, id?: string }|null} props.accountUser
 * @param {object} [props.homeSelectedTierData] — defaults to PRICING_TIERS[0]
 * @param {object} [props.homeSelectedSubPlan] — defaults to the plan matching homeSelectedSubTier
 */
const PricingSection = forwardRef(function PricingSection(
  {
    homePaymentType,
    setHomePaymentType,
    homePpuQuantity,
    setHomePpuQuantity,
    homeSelectedSubTier,
    setHomeSelectedSubTier,
    homePromoCode,
    setHomePromoCode,
    homeAppliedCoupon,
    homeCouponError,
    homeCouponLoading,
    homeCheckoutLoading,
    onApplyCoupon,
    onRemoveCoupon,
    onCheckout,
    accountUser,
    homeSelectedTierData,
    homeSelectedSubPlan,
  },
  ref
) {
  const tierData = homeSelectedTierData || PRICING_TIERS[0];
  const subPlan =
    homeSelectedSubPlan || SUBSCRIPTION_PLANS.find((p) => p.id === homeSelectedSubTier) || SUBSCRIPTION_PLANS[0];
  const oneTimeTotal = homePpuQuantity * 1.0;

  return (
    <section
      ref={ref}
      id="plans-and-credits"
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 mt-20 md:mt-28 mb-8 border-t border-white/[0.06] scroll-mt-24"
    >
      <div className="text-center mb-12 max-w-2xl mx-auto">
        <Eyebrow className="mb-4">Pricing</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gradient-insight tracking-tight leading-tight mb-4">
          Plans &amp; credits
        </h2>
        <p className="text-base text-critic-text-secondary font-normal leading-relaxed">
          Credits or a monthly plan — same full analysis every time. Checkout on Stripe.
        </p>
      </div>
      <div className="max-w-6xl mx-auto">
      <div className="relative rounded-[1.75rem] md:rounded-[2rem] border border-white/[0.09] bg-gradient-to-b from-white/[0.05] via-critic-bg-card/35 to-critic-surface-deep shadow-[0_32px_120px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.06)] overflow-hidden">
        <div
          className="pointer-events-none absolute -top-40 -right-20 h-[28rem] w-[28rem] rounded-full bg-gradient-to-bl from-apple-blue/25 via-critic-insight-mid/12 to-transparent blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-20 h-[22rem] w-[22rem] rounded-full bg-gradient-to-tr from-critic-insight-purple/20 via-apple-purple/5 to-transparent blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 h-[min(100%,480px)] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.08),transparent_65%)]"
          aria-hidden
        />

        <div className="relative px-5 py-10 sm:px-8 sm:py-12 md:px-12 md:py-14">
          <div className="max-w-xl mx-auto mb-10 md:mb-12">
            <SegmentedControl
              layout="equal"
              ariaLabel="Payment type"
              className="bg-black/30 border-white/[0.1]"
              value={homePaymentType}
              onChange={setHomePaymentType}
              options={[
                { value: 'one-time', label: 'Pay per use' },
                { value: 'subscription', label: 'Subscribe' },
              ]}
            />
          </div>

          {homePaymentType === 'one-time' && (
            <div id="purchase-credits" className="mb-8 scroll-mt-28 space-y-8">
              <p className="text-center text-sm text-critic-text-secondary max-w-lg mx-auto leading-relaxed">
                One credit equals one full review — same depth as subscription. Buy any quantity; credits stay on
                your account until you use them.
              </p>
              <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
              <div className="rounded-2xl border border-white/[0.1] bg-black/25 backdrop-blur-sm p-6 sm:p-8 flex flex-col justify-between min-h-[280px]">
                <div>
                  <p className="text-xs uppercase tracking-wider text-critic-text-muted font-semibold mb-2">
                    Credits
                  </p>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold text-white mb-2">
                    Buy only what you need
                  </h3>
                  {tierData.subtitle && (
                    <p className="text-sm text-critic-text-muted font-medium leading-snug mb-2">
                      {tierData.subtitle}
                    </p>
                  )}
                  <p className="text-sm text-critic-text-secondary leading-relaxed mb-8">
                    {tierData.description}
                  </p>
                  <div className="flex items-center justify-center gap-3 sm:gap-6 mb-6">
                    <button
                      type="button"
                      onClick={() => setHomePpuQuantity((q) => Math.max(1, q - 1))}
                      className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl bg-white/[0.06] border border-white/[0.1] text-white text-xl font-medium hover:bg-white/[0.1] hover:border-white/[0.15] transition-all active:scale-95"
                      aria-label="Decrease credits"
                    >
                      −
                    </button>
                    <div className="text-center min-w-[140px] px-2">
                      <div className="text-4xl sm:text-5xl font-bold text-white tabular-nums tracking-tight">
                        {homePpuQuantity}
                      </div>
                      <div className="text-sm text-critic-text-muted mt-1">credits at $1 USD each</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setHomePpuQuantity((q) => Math.min(5000, q + 1))}
                      className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl bg-white/[0.06] border border-white/[0.1] text-white text-xl font-medium hover:bg-white/[0.1] hover:border-white/[0.15] transition-all active:scale-95"
                      aria-label="Increase credits"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="pt-6 border-t border-white/[0.07]">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <p className="text-xs text-critic-text-muted mb-1">Total due</p>
                      <p className="font-display text-3xl sm:text-4xl font-semibold bg-gradient-to-r from-white via-white to-zinc-300 bg-clip-text text-transparent">
                        ${oneTimeTotal.toFixed(2)}
                        <span className="text-lg font-normal text-critic-text-muted"> USD</span>
                      </p>
                    </div>
                    <p className="text-xs text-critic-text-muted text-right max-w-[140px]">
                      No subscription · Credits don&apos;t expire on our side
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-5">
                <div className="rounded-2xl border border-white/[0.1] bg-gradient-to-b from-white/[0.04] to-transparent p-6 sm:p-8 flex-1">
                  <p className="text-xs uppercase tracking-wider text-critic-text-muted font-semibold mb-4">
                    Every credit includes
                  </p>
                  <ul className="space-y-3.5">
                    {[
                      'Full AI review — same depth as subscription',
                      'Technical & artistic scoring',
                      'Music tracks & book manuscripts',
                      'Structured feedback you can act on',
                    ].map((line) => (
                      <li key={line} className="flex items-start gap-3 text-sm text-critic-text-secondary">
                        <CheckCircle className="h-5 w-5 text-emerald-400/90 shrink-0 mt-0.5" aria-hidden />
                        <span className="leading-relaxed">{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-2xl border border-white/[0.1] bg-critic-bg-card/40 p-5 sm:p-6">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-400/90" aria-hidden />
                    Promo code
                  </h3>
                  {!homeAppliedCoupon ? (
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="text"
                          value={homePromoCode}
                          onChange={(e) => setHomePromoCode(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          className="flex-1 px-4 py-3 rounded-xl bg-black/35 border border-white/[0.1] text-white placeholder-critic-text-muted text-sm focus:outline-none focus:ring-2 focus:ring-apple-blue/60 focus:border-transparent"
                        />
                        <button
                          type="button"
                          onClick={onApplyCoupon}
                          disabled={!homePromoCode?.trim() || homeCouponLoading}
                          className="px-5 py-3 rounded-xl bg-white text-black font-semibold text-sm hover:bg-zinc-100 disabled:bg-white/10 disabled:text-white/40 disabled:cursor-not-allowed transition-colors shrink-0"
                        >
                          Apply
                        </button>
                      </div>
                      {homeCouponError && <p className="text-red-400 text-sm">{homeCouponError}</p>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-emerald-950/40 border border-emerald-600/30 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="h-6 w-6 text-emerald-400 shrink-0" />
                        <div>
                          <p className="text-emerald-300 font-semibold">{homeAppliedCoupon.id}</p>
                          <p className="text-emerald-400/80 text-sm">
                            {homeAppliedCoupon.percent_off
                              ? `${homeAppliedCoupon.percent_off}% off`
                              : `$${homeAppliedCoupon.amount_off / 100} off`}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={onRemoveCoupon}
                        className="p-2 rounded-lg text-critic-text-muted hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Remove promo code"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              </div>
            </div>
          )}

          {homePaymentType === 'subscription' && (
            <div className="mb-8">
              <p className="text-center text-sm text-critic-text-secondary max-w-lg mx-auto mb-8 leading-relaxed">
                One predictable bill per period. Analyses refresh each billing cycle — ideal if you ship often.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5 items-stretch">
                {SUBSCRIPTION_PLANS.map((plan) => {
                  const selected = homeSelectedSubTier === plan.id;
                  return (
                    <button
                      type="button"
                      key={plan.id}
                      onClick={() => setHomeSelectedSubTier(plan.id)}
                      className={`relative text-left rounded-2xl border p-6 sm:p-7 flex flex-col transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-apple-blue focus-visible:ring-offset-2 focus-visible:ring-offset-critic-surface-deep ${
                        selected
                          ? 'border-apple-blue/80 bg-gradient-to-b from-apple-blue/20 via-critic-insight-mid/10 to-transparent shadow-[0_20px_60px_rgba(0,122,255,0.15)] scale-[1.02] md:scale-105 z-10'
                          : 'border-white/[0.08] bg-black/20 hover:border-white/[0.16] hover:bg-white/[0.03]'
                      }`}
                    >
                      {plan.popular && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] font-semibold text-white/95 bg-gradient-to-r from-apple-blue to-critic-insight-purple px-4 py-1.5 rounded-full shadow-lg">
                          Popular
                        </span>
                      )}
                      <div className="mb-5 pt-1">
                        <p className="text-xs uppercase tracking-wider text-critic-text-muted font-semibold mb-1">
                          {plan.name}
                        </p>
                        <div className="flex items-baseline gap-1 flex-wrap">
                          <span className="font-display text-4xl font-bold text-white">${plan.price}</span>
                          <span className="text-critic-text-muted text-sm">/ period</span>
                        </div>
                        <p className="text-sm text-critic-text-secondary mt-2 leading-snug">{plan.description}</p>
                      </div>
                      <div className="h-px w-full bg-white/[0.08] mb-5" />
                      <ul className="space-y-2.5 flex-1 mb-2">
                        <li className="flex items-start gap-2.5 text-xs sm:text-sm text-critic-text-secondary">
                          <CheckCircle className="h-4 w-4 text-emerald-400/90 shrink-0 mt-0.5" aria-hidden />
                          <span>
                            <strong className="text-critic-text-primary font-medium">{plan.analyses}</strong>{' '}
                            analyses per billing period
                          </span>
                        </li>
                        <li className="flex items-start gap-2.5 text-xs sm:text-sm text-critic-text-secondary">
                          <CheckCircle className="h-4 w-4 text-emerald-400/90 shrink-0 mt-0.5" aria-hidden />
                          <span>Same AI depth as pay-per-use</span>
                        </li>
                        <li className="flex items-start gap-2.5 text-xs sm:text-sm text-critic-text-secondary">
                          <CheckCircle className="h-4 w-4 text-emerald-400/90 shrink-0 mt-0.5" aria-hidden />
                          <span>Music &amp; books — one workspace</span>
                        </li>
                        <li className="flex items-start gap-2.5 text-xs sm:text-sm text-critic-text-secondary">
                          <CheckCircle className="h-4 w-4 text-emerald-400/90 shrink-0 mt-0.5" aria-hidden />
                          <span>Manage plan in Stripe anytime</span>
                        </li>
                      </ul>
                      {selected && (
                        <p className="text-[11px] text-apple-blue font-medium mt-4 flex items-center gap-1.5">
                          <span className="inline-block h-1.5 w-1.5 rounded-full bg-apple-blue" aria-hidden />
                          Selected for checkout
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {homePaymentType === 'subscription' && !accountUser?.email && (
            <Alert tone="warning" className="mb-6 rounded-xl border-amber-500/25 bg-amber-500/5 text-amber-100/90 text-center">
              Sign in with Google (header) so we can attach your subscription to your account. Subscription
              checkout requires an email on file.
            </Alert>
          )}

          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] px-5 py-4 sm:px-6 mb-6">
            <h4 className="text-white font-medium text-sm mb-2 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-critic-text-muted" aria-hidden />
              How checkout works
            </h4>
            <ul className="text-xs sm:text-sm text-critic-text-secondary space-y-1.5 leading-relaxed">
              <li className="flex gap-2">
                <span className="text-critic-text-muted shrink-0">1.</span>
                <span>You&apos;ll be redirected to Stripe&apos;s secure hosted checkout.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-critic-text-muted shrink-0">2.</span>
                <span>Complete payment there — don&apos;t close the tab until it confirms.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-critic-text-muted shrink-0">3.</span>
                <span>You&apos;ll return to CriticAI automatically when done.</span>
              </li>
            </ul>
          </div>

          <button
            type="button"
            onClick={onCheckout}
            disabled={
              homeCheckoutLoading ||
              (homePaymentType === 'subscription' &&
                (!accountUser?.email || !accountUser?.id))
            }
            className="group w-full flex items-center justify-center gap-2 rounded-2xl bg-gradient-insight px-6 py-4 text-[15px] font-semibold text-white shadow-[0_16px_48px_rgba(0,122,255,0.35)] hover:shadow-[0_20px_56px_rgba(0,122,255,0.45)] hover:brightness-[1.05] disabled:opacity-45 disabled:cursor-not-allowed disabled:shadow-none transition-all duration-300"
          >
            {homeCheckoutLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white" />
                <span>Redirecting to Stripe…</span>
              </>
            ) : homePaymentType === 'subscription' &&
              (!accountUser?.email || !accountUser?.id) ? (
              <>
                <Lock className="h-5 w-5" />
                <span>Sign in to continue</span>
              </>
            ) : (
              <>
                <span>
                  {homePaymentType === 'subscription'
                    ? `Continue to Stripe — $${subPlan.price} / period · ${subPlan.analyses} analyses`
                    : `Continue to Stripe — $${oneTimeTotal.toFixed(2)} USD`}
                </span>
                <ChevronRight className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </button>

          <p className="text-center text-[11px] sm:text-xs text-critic-text-muted mt-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <span>Payments processed by Stripe</span>
            <span className="hidden sm:inline text-white/15" aria-hidden>
              ·
            </span>
            <span>Encrypted · Major cards supported</span>
          </p>
        </div>
      </div>
      </div>
    </section>
  );
});

export default PricingSection;
